/**
 * IndexedDB-based offline storage service using Dexie
 * Handles large data including work orders, photos, and other binary assets.
 *
 * This service replaces localStorage for offline data to support:
 * - Much larger storage capacity (no 5-10MB limit)
 * - Binary data storage (photos, files)
 * - Better performance for large datasets
 * - Structured queries with indexes
 * - Type safety with TypeScript
 */

import { Dexie } from 'dexie';

export interface OfflineDataEntry<T = any> {
  id: string;
  data: T;
  timestamp: number;
  expires?: number;
  metadata?: Record<string, any>;
}

export interface PhotoEntry {
  id: string;
  workId: string;
  blob: Blob;
  fileName: string;
  mimeType: string;
  size: number;
  timestamp: number;
  thumbnail?: Blob;
}

export interface WorkEntry {
  id: string;
  data: any;
  lastModified: number;
  pendingChanges?: any;
  photoIds: string[];
  syncStatus: 'synced' | 'pending' | 'conflict';
}

export interface AuthEntry {
  key: string;
  value: any;
  expires?: number;
}

export interface I18nEntry {
  id: string; // composite key: username-language
  username: string;
  language: string;
  labels: any;
  timestamp: number;
}

export interface MetadataEntry {
  key: string;
  value: any;
  timestamp: number;
}

class IndexedDBOfflineService extends Dexie {
  // Declare all tables
  auth!: Dexie.Table<AuthEntry, string>;
  cache!: Dexie.Table<OfflineDataEntry, string>;
  workorders!: Dexie.Table<WorkEntry, string>;
  photos!: Dexie.Table<PhotoEntry, string>;
  i18n!: Dexie.Table<I18nEntry, string>;
  metadata!: Dexie.Table<MetadataEntry, string>;

  constructor() {
    super('FMSOfflineDB');

    this.version(1).stores({
      auth: 'key, expires',
      cache: 'id, expires, timestamp',
      workorders: 'id, lastModified, syncStatus',
      photos: 'id, workId, timestamp, size',
      i18n: 'id, username, timestamp',
      metadata: 'key, timestamp',
    });
  }

  // Auth operations (replacement for localStorage auth data)
  async setAuthData(
    key: string,
    value: any,
    expiresIn?: number
  ): Promise<void> {
    const authEntry: AuthEntry = {
      key,
      value,
      ...(expiresIn && { expires: Date.now() + expiresIn }),
    };

    await this.auth.put(authEntry);
  }

  async getAuthData(key: string): Promise<any> {
    const entry = await this.auth.where('key').equals(key).first();

    if (!entry) return null;

    // Check expiration
    if (entry.expires && Date.now() > entry.expires) {
      await this.auth.where('key').equals(key).delete();
      return null;
    }

    return entry.value;
  }

  async removeAuthData(key: string): Promise<void> {
    await this.auth.where('key').equals(key).delete();
  }

  // Cache operations (replacement for localStorage cache)
  async setCacheData(
    id: string,
    data: any,
    expiresIn: number = 5 * 60 * 1000
  ): Promise<void> {
    const cacheEntry: OfflineDataEntry = {
      id,
      data,
      timestamp: Date.now(),
      expires: Date.now() + expiresIn,
    };

    await this.cache.put(cacheEntry);
  }

  async getCacheData(id: string): Promise<any> {
    const entry = await this.cache.where('id').equals(id).first();

    if (!entry) return null;

    // Check expiration
    if (entry.expires && Date.now() > entry.expires) {
      await this.cache.where('id').equals(id).delete();
      return null;
    }

    return entry.data;
  }

  async clearExpiredCache(): Promise<number> {
    const now = Date.now();
    const expiredEntries = await this.cache
      .where('expires')
      .below(now)
      .toArray();

    if (expiredEntries.length > 0) {
      const expiredIds = expiredEntries.map(
        (entry: OfflineDataEntry) => entry.id
      );
      await this.cache.where('id').anyOf(expiredIds).delete();
    }

    return expiredEntries.length;
  }

  // Work order operations
  async setWork(work: WorkEntry): Promise<void> {
    await this.workorders.put(work);
  }

  async getWork(id: string): Promise<WorkEntry | undefined> {
    return await this.workorders.where('id').equals(id).first();
  }

  async getAllWorks(): Promise<WorkEntry[]> {
    return await this.workorders.toArray();
  }

  async getPendingWorks(): Promise<WorkEntry[]> {
    return await this.workorders
      .where('syncStatus')
      .equals('pending')
      .toArray();
  }

  async deleteWork(id: string): Promise<void> {
    // Also delete associated photos
    const photos = await this.getPhotosForWork(id);
    if (photos.length > 0) {
      const photoIds = photos.map((photo: PhotoEntry) => photo.id);
      await this.photos.where('id').anyOf(photoIds).delete();
    }

    await this.workorders.where('id').equals(id).delete();
  }

  // Photo operations for binary data
  async setPhoto(photo: PhotoEntry): Promise<void> {
    await this.photos.put(photo);
  }

  async getPhoto(id: string): Promise<PhotoEntry | undefined> {
    return await this.photos.where('id').equals(id).first();
  }

  async getPhotosForWork(workId: string): Promise<PhotoEntry[]> {
    return await this.photos.where('workId').equals(workId).toArray();
  }

  async deletePhoto(id: string): Promise<void> {
    await this.photos.where('id').equals(id).delete();
  }

  // Optimized photo storage with thumbnail generation
  async storePhotoWithThumbnail(
    id: string,
    workId: string,
    file: File,
    thumbnailMaxSize: number = 200
  ): Promise<void> {
    // Create thumbnail
    const thumbnail = await this.createThumbnail(file, thumbnailMaxSize);

    const photoEntry: PhotoEntry = {
      id,
      workId,
      blob: file,
      fileName: file.name,
      mimeType: file.type,
      size: file.size,
      timestamp: Date.now(),
      thumbnail,
    };

    await this.setPhoto(photoEntry);
  }

  private async createThumbnail(file: File, maxSize: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate thumbnail dimensions
        const { width, height } = this.calculateThumbnailSize(
          img.width,
          img.height,
          maxSize
        );

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create thumbnail'));
            }
          },
          'image/jpeg',
          0.8
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  private calculateThumbnailSize(
    originalWidth: number,
    originalHeight: number,
    maxSize: number
  ) {
    const aspectRatio = originalWidth / originalHeight;

    if (originalWidth > originalHeight) {
      return {
        width: Math.min(maxSize, originalWidth),
        height: Math.min(maxSize, originalWidth) / aspectRatio,
      };
    } else {
      return {
        width: Math.min(maxSize, originalHeight) * aspectRatio,
        height: Math.min(maxSize, originalHeight),
      };
    }
  }

  // I18n operations
  async setI18nLabels(
    username: string,
    language: string,
    labels: any
  ): Promise<void> {
    const id = `${username}-${language}`;
    const i18nEntry: I18nEntry = {
      id,
      username,
      language,
      labels,
      timestamp: Date.now(),
    };

    await this.i18n.put(i18nEntry);
  }

  async getI18nLabels(username: string, language: string): Promise<any> {
    const id = `${username}-${language}`;
    const entry = await this.i18n.where('id').equals(id).first();
    return entry?.labels || null;
  }

  async deleteI18nLabels(username: string, language?: string): Promise<void> {
    if (language) {
      const id = `${username}-${language}`;
      await this.i18n.where('id').equals(id).delete();
    } else {
      // Delete all languages for user
      await this.i18n.where('username').equals(username).delete();
    }
  }

  // Storage management and statistics
  async getStorageStats(): Promise<{
    totalSize: number;
    authEntries: number;
    cacheEntries: number;
    workEntries: number;
    photoEntries: number;
    photoSize: number;
    i18nEntries: number;
  }> {
    const [authEntries, cacheEntries, workEntries, photoEntries, i18nEntries] =
      await Promise.all([
        this.auth.count(),
        this.cache.count(),
        this.workorders.count(),
        this.photos.count(),
        this.i18n.count(),
      ]);

    // Calculate photo size
    const photos = await this.photos.toArray();
    const photoSize = photos.reduce(
      (total: number, photo: PhotoEntry) => total + photo.size,
      0
    );

    // Estimate other data sizes (approximate)
    const authDataSize = authEntries * 100; // ~100 bytes per auth entry
    const cacheDataSize = cacheEntries * 1000; // ~1KB per cache entry
    const workDataSize = workEntries * 2000; // ~2KB per work order
    const i18nDataSize = i18nEntries * 500; // ~500 bytes per i18n entry

    const totalSize =
      photoSize + authDataSize + cacheDataSize + workDataSize + i18nDataSize;

    return {
      totalSize,
      authEntries,
      cacheEntries,
      workEntries,
      photoEntries,
      photoSize,
      i18nEntries,
    };
  }

  async cleanup(
    options: {
      clearExpiredCache?: boolean;
      maxPhotoAge?: number; // milliseconds
      maxCacheSize?: number; // bytes
    } = {}
  ): Promise<{
    deletedCacheEntries: number;
    deletedPhotos: number;
    freedSpace: number;
  }> {
    let deletedCacheEntries = 0;
    let deletedPhotos = 0;
    let freedSpace = 0;

    // Clear expired cache
    if (options.clearExpiredCache !== false) {
      deletedCacheEntries = await this.clearExpiredCache();
    }

    // Clean old photos
    if (options.maxPhotoAge) {
      const cutoff = Date.now() - options.maxPhotoAge;
      const oldPhotos = await this.photos
        .where('timestamp')
        .below(cutoff)
        .toArray();

      for (const photo of oldPhotos) {
        freedSpace += photo.size;
        deletedPhotos++;
      }

      if (oldPhotos.length > 0) {
        const oldPhotoIds = oldPhotos.map((photo: PhotoEntry) => photo.id);
        await this.photos.where('id').anyOf(oldPhotoIds).delete();
      }
    }

    return {
      deletedCacheEntries,
      deletedPhotos,
      freedSpace,
    };
  }
}

// Export singleton instance
export const indexedDBOfflineService = new IndexedDBOfflineService();

// Convenience functions for backward compatibility
export const offlineStorage = {
  // Auth operations
  setAuthData: (key: string, value: any, expiresIn?: number) =>
    indexedDBOfflineService.setAuthData(key, value, expiresIn),
  getAuthData: (key: string) => indexedDBOfflineService.getAuthData(key),
  removeAuthData: (key: string) => indexedDBOfflineService.removeAuthData(key),

  // Cache operations
  setCacheData: (id: string, data: any, expiresIn?: number) =>
    indexedDBOfflineService.setCacheData(id, data, expiresIn),
  getCacheData: (id: string) => indexedDBOfflineService.getCacheData(id),

  // I18n operations
  setI18nLabels: (username: string, language: string, labels: any) =>
    indexedDBOfflineService.setI18nLabels(username, language, labels),
  getI18nLabels: (username: string, language: string) =>
    indexedDBOfflineService.getI18nLabels(username, language),

  // Storage management
  getStorageStats: () => indexedDBOfflineService.getStorageStats(),
  cleanup: (options?: any) => indexedDBOfflineService.cleanup(options),
};
